import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';

import { Button, Flex, Typography } from 'antd';

import isEmpty from 'lodash.isempty';

import axios from '../../../axios';

import { isShapeInsideBoundary } from '../../../helpers/map';

import { MAIN_GROUP_TYPE } from '../../../constants/group';
import { MAPS_TAB } from '../../../constants/map';
import messages from '../../../constants/messages';

import { useUnsavedChangesPrompt } from '../../../hooks/useUnsavedChangesPrompt';

import { fetchFarm } from '../../../redux/actions/farm';
import { deleteGeofence, fetchGeofence, fetchGeofences, updateGeofence } from '../../../redux/actions/geofence';
import { fetchRotations } from '../../../redux/actions/geofence-rotations';
import { fetchGrazingData, fetchGrazingHeatmapStyles } from '../../../redux/actions/grazing';
import { fetchLabels } from '../../../redux/actions/label';

import AppModal from '../../../components/AppModal';
import CancelSaveButtons from '../../../components/CancelSaveButtons';
import GeofenceEditForm from '../../../components/Geofence/GeofenceEditForm';
import LoadingBar from '../../../components/LoadingBar';
import MapGeofence from '../../../components/Maps/MapGeofence';
import { DEFAULT_SHAPE_COLOR } from '../../../components/Maps/constants';
import ChooseGroupsModal from '../../../components/Modals/ChooseGroupsModal';
import PrevNextButtons from '../../../components/PrevNextButtons';
import { errorToastHandler, successToastHandler, warnToastHandler } from '../../../components/action_notifier';
import UnsavedChangesModal from '../../../components/ui/UnsavedChangesModal';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';
import AppDropdown from '../../ui-components/AppDropdown';

import GeofenceDetails from './GeofenceDetails';
import GeofenceHistory from './GeofenceHistory';

const { Title } = Typography;

export default function Geofence() {
  const history = useHistory();
  const { id } = useParams();

  const dispatch = useDispatch();

  const formRef = useRef(null);

  const { data: grazing } = useSelector(state => state.grazing);
  const { data: labels } = useSelector(state => state.label);
  const { farm } = useSelector(state => state.farm);
  const { geofence } = useSelector(state => state.geofence);
  const [geofenceSizes, setGeofenceSizes] = useState({ size: 0, circumference: 0 });
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submittable, setSubmittable] = useState(false);
  const [state, setState] = useState({
    chooseGroupModal: false,
    color: DEFAULT_SHAPE_COLOR,
    delete_modal: false,
    editing: false,
    farm: {},
    geofence: {
      id: null,
      farm_id: null,
      name: '',
      size: 0,
      animals_count: 0,
      description: '',
      circumference: 0,
      position: [24.984627, 31.717946],
      geo_json: {}
    },
    geofence_edit: {
      id: null,
      farm_id: null,
      name: '',
      size: 0,
      animals_count: 0,
      description: '',
      circumference: 0,
      position: [24.984627, 31.717946],
      geo_json: {}
    },
    groupOptions: [],
    isOpenConfirmationModal: false,
    savedGroups: [],
    selectedGroups: [],
    selected_geofences: [],
    isNew: false
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (geofence) {
      getFarm();
      getGrazingData();
      getLabels();

      setGeofenceSizes({ circumference: geofence.circumference, size: geofence.size });
      setState(prevState => ({
        ...prevState,
        color: geofence.color,
        geofence: structuredClone(geofence),
        geofence_edit: structuredClone(geofence),
        savedGroups: geofence?.labels?.length ? geofence?.labels?.map(l => ({ label: l?.name, value: l?.id })) : null
      }));
    }
  }, [geofence]);

  useEffect(() => {
    if (farm && geofence) {
      setState(prevState => ({
        ...prevState,
        color: geofence.color,
        geofence: {
          ...geofence,
          features: farm.geofences,
          sites: farm.sites
        },
        geofence_edit: {
          ...geofence,
          features: farm.geofences,
          sites: farm.sites
        },
        farm: structuredClone(farm),
        selectedGroups: geofence?.labels?.length
          ? geofence?.labels?.map(l => ({ label: l?.name, value: l?.id }))
          : null,
        savedGroups: geofence?.labels?.length ? geofence?.labels?.map(l => ({ label: l?.name, value: l?.id })) : null
      }));
    }
  }, [farm]);

  useEffect(() => {
    if (labels) {
      setState(prevState => ({
        ...prevState,
        groupOptions: labels
          .filter(label => label.farm?.id === state.geofence.farm_id)
          .map(x => ({ value: x.id, label: x.name, color: x.colour }))
      }));
    }
  }, [labels]);

  const changeColor = useCallback(
    color => {
      setState(prevState => ({ ...prevState, color }));
    },
    [state.color]
  );

  const toggleGroupModal = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      selectedGroups: state.savedGroups,
      chooseGroupModal: !prevState.chooseGroupModal
    }));
  }, [state.chooseGroupModal]);

  const toggleDeleteModal = useCallback(() => {
    setState(prevState => ({ ...prevState, delete_modal: !prevState.delete_modal }));
  }, [state.delete_modal]);

  const toggleEditMode = useCallback(() => {
    setState(prevState => ({ ...prevState, editing: !prevState.editing }));
  }, [state.editing]);

  const toggleConfirmationModal = useCallback(() => {
    setState(prevState => ({ ...prevState, isOpenConfirmationModal: !prevState.isOpenConfirmationModal }));
  }, [state.isOpenConfirmationModal]);

  const navigateToThePrevPage = useCallback(() => {
    history.push(`/pastures/${state.geofence.prev_id}`);
  }, [state.geofence.prev_id]);

  const navigateToTheNextPage = useCallback(() => {
    history.push(`/pastures/${state.geofence.next_id}`);
  }, [state.geofence.next_id]);

  const handleGeofenceGroupUpdate = useCallback(() => {
    updateGeofenceGroup();
    toggleConfirmationModal();
  }, [state.selectedGroups]);

  const handleGeofenceGeoJsonChange = useCallback(
    geo_json => {
      setIsFormDirty(true);
      setState(prevState => ({ ...prevState, geofence_edit: { ...prevState.geofence_edit, geo_json } }));
    },
    [state.geofence_edit.geo_json]
  );

  const deleteGeoJson = useCallback(() => {
    setIsFormDirty(true);
    setState(prevState => ({ ...prevState, geofence_edit: { ...prevState.geofence_edit, geo_json: {} } }));
    setSubmittable(false);
    warnToastHandler(messages.MASTER_GEOFENCE_REQUIRED);
  }, []);

  async function getFarm() {
    try {
      await dispatch(fetchFarm(geofence?.farm_id)).unwrap();
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function getLabels() {
    dispatch(fetchLabels({ farm_ids: geofence?.farm_id, main_type: MAIN_GROUP_TYPE.PHYSICAL }));
  }

  async function getGeofence() {
    dispatch(fetchGeofence(id));
  }

  async function getGrazingData() {
    const rotationId = geofence?.currentRotation ? geofence?.currentRotation?.id : geofence?.lastRotation?.id;

    if (!rotationId) return;

    try {
      await dispatch(fetchGrazingData({ rotationIds: [rotationId] })).unwrap();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function fetchData() {
    try {
      await Promise.all([
        getGeofence(),
        // getLabels(),
        dispatch(fetchGeofences()),
        dispatch(fetchGrazingHeatmapStyles()),
        dispatch(fetchRotations({ geofence_id: id }))
      ]);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    } finally {
      setLoading(false);
    }
  }

  async function deleteCurrentGeofence() {
    try {
      await dispatch(deleteGeofence(id)).unwrap();
      successToastHandler(messages.ARCHIVED);
      history.push(`/maps?tab=${MAPS_TAB.GEOFENCE}`);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_DELETE_DATA);
    }
  }

  async function updateCurrentGeofence() {
    const geofenceNewValues = await formRef.current?.validateFields();

    try {
      if (!id) return;

      const updatedGeofence = state.isNew
        ? state.geofence
        : {
            ...state.geofence_edit,
            ...geofenceNewValues
          };

      if (isEmpty(updatedGeofence.geo_json)) {
        warnToastHandler(messages.DATA_REQUIRED);

        return;
      }

      const boundary = updatedGeofence?.is_master
        ? updatedGeofence?.geo_json
        : state.geofence?.features?.find(feature => feature.is_master)?.geo_json;
      const shapes = updatedGeofence?.is_master
        ? updatedGeofence?.features.filter(feature => !feature.is_master).map(feature => feature.geo_json)
        : updatedGeofence?.geo_json;

      if (!isShapeInsideBoundary(boundary, shapes)) {
        warnToastHandler(
          updatedGeofence?.is_master ? messages.GEOFENCE_OUTSIDE_MASTER : messages.SHAPE_OUTSIDE_BOUNDARIES('pasture')
        );

        return;
      }

      await dispatch(updateGeofence({ id, data: { ...updatedGeofence, color: state.color } })).unwrap();

      setState(prevState => ({ ...prevState, editing: false }));
      setIsFormDirty(false);
      successToastHandler(messages.UPDATED);
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_UPDATE_DATA);
    }
  }

  async function updateGeofenceGroup() {
    const groupIds = state.selectedGroups.map(g => g.value);

    if (groupIds?.length) {
      try {
        await axios.put(`geofences/updateGroupGeofence/${id}`, { group_ids: groupIds });
        await getGeofence();
      } catch (error) {
        errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_UPDATE_DATA);
      }
    }

    toggleGroupModal();
  }

  const actions = [
    {
      label: (
        <Button color="default" size="small" variant="link" onClick={toggleEditMode}>
          Edit pasture
        </Button>
      )
    },
    {
      label: (
        <Button color="default" size="small" variant="link" disabled={geofence?.is_master} onClick={toggleGroupModal}>
          Edit groups in pasture
        </Button>
      )
    },
    {
      label: (
        <Button color="default" size="small" variant="link" onClick={toggleDeleteModal}>
          Archive pasture
        </Button>
      )
    }
  ];

  const onSelectGroup = (values, options) => {
    setState(prevState => ({ ...prevState, selectedGroups: [...options] }));
  };

  const setGeofence = useCallback(value => {
    setIsFormDirty(true);
    setState(prevState => ({
      ...prevState,
      geofence: {
        ...prevState.geofence,
        geo_json: value
      },
      isNew: true
    }));
  }, []);

  const validate = useCallback(() => {
    const geofenceName = formRef?.current?.getFieldValue('name');

    if (typeof geofenceName === 'string' && geofenceName.trim() !== '') {
      setSubmittable(true);
    }
  }, [formRef]);

  const name = state.geofence?.is_master ? state.geofence?.farm?.name : state.geofence.name;
  const breadcrumbItems = [
    {
      title: <Link to={`/maps?tab=${MAPS_TAB.GEOFENCE}`}>List of Pastures</Link>
    },
    {
      title: `${state.editing ? 'Edit ' : ''}Pasture ${name}`
    }
  ];

  const { NavigationPrompt, modalProps, nextLocation, confirmed, triggerUnsavedChangesPrompt } =
    useUnsavedChangesPrompt({
      when: isFormDirty,
      onSaveAndLeave: async () => {
        if (submittable) {
          await updateCurrentGeofence();

          return true;
        } else {
          warnToastHandler(messages.DATA_REQUIRED);
          formRef.current?.validateFields();

          return false;
        }
      },
      onDiscardAndLeave: () => {
        setIsFormDirty(false);
        toggleEditMode();
      }
    });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  const handleCancel = useCallback(() => {
    isFormDirty ? triggerUnsavedChangesPrompt() : toggleEditMode();
  }, [isFormDirty]);

  async function getRotations(query) {
    try {
      await Promise.all([dispatch(fetchRotations({ geofence_id: id, query }))]);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  const handleShapeSizes = useCallback(sizes => {
    setGeofenceSizes(sizes);
  }, []);

  return (
    <>
      {loading ? (
        <LoadingBar fullscreen={true} />
      ) : (
        <>
          {NavigationPrompt}
          <Flex gap="small" vertical={false} justify="space-between" wrap>
            <Flex vertical={true}>
              <Title level={4}>
                {state.editing && 'Edit '}Pasture - {name}
              </Title>

              <AppBreadcrumb items={breadcrumbItems} />
            </Flex>
            <Flex gap="small">
              {!state.editing && <AppDropdown label="Actions" items={actions} />}
              {state.editing && (
                <CancelSaveButtons
                  disabled={!submittable}
                  handleCancel={handleCancel}
                  handleSave={updateCurrentGeofence}
                />
              )}

              <PrevNextButtons
                nextId={state.geofence.next_id}
                prevId={state.geofence.prev_id}
                handlePrev={navigateToThePrevPage}
                handleNext={navigateToTheNextPage}
              />
            </Flex>
          </Flex>

          <MapGeofence
            color={state.color}
            editing={state.editing}
            geofence={state.editing ? state.geofence_edit : state.geofence}
            grazing={grazing}
            isNew={false}
            handleCancelEditing={toggleEditMode}
            handleCreate={setGeofence}
            handleDelete={deleteGeoJson}
            handleSaveEditing={updateCurrentGeofence}
            handleShapeSizes={handleShapeSizes}
            onEdit={handleGeofenceGeoJsonChange}
            setSubmittable={validate}
          />

          {state.editing && (
            <GeofenceEditForm
              circumference={geofenceSizes?.circumference}
              color={state.color}
              defaultName={state.geofence_edit?.name}
              description={state.geofence_edit?.description}
              carrying_capacity={state.geofence?.carrying_capacity}
              farmId={state.geofence?.farm?.name}
              formRef={formRef}
              isMaster={state.geofence?.is_master}
              name={state.geofence?.name}
              size={geofenceSizes?.size}
              handleColorChange={changeColor}
              onChange={setIsFormDirty}
              setSubmittable={setSubmittable}
            />
          )}

          {!state.editing && (
            <GeofenceDetails geofence={state.geofence} description={state.geofence_edit.description} />
          )}

          {state.editing && (
            <Flex justify="end" className="mb-2">
              <CancelSaveButtons
                disabled={!submittable}
                handleCancel={handleCancel}
                handleSave={updateCurrentGeofence}
              />
            </Flex>
          )}

          <GeofenceHistory
            geofences={[geofence]}
            geofenceId={id}
            fetchData={getRotations}
            handleSearchChange={getRotations}
          />

          <ChooseGroupsModal
            isOpen={state.chooseGroupModal}
            options={state.groupOptions}
            values={state.selectedGroups}
            handleGroupsChange={onSelectGroup}
            onCancel={toggleGroupModal}
            onSave={toggleConfirmationModal}
          />

          <AppModal
            confirmButtonColor="danger"
            confirmButtonText="Archive"
            title="Archive pasture"
            isOpen={state.delete_modal}
            handleCancel={toggleDeleteModal}
            handleConfirm={deleteCurrentGeofence}>
            <div className="py-4">{messages.ARCHIVE_CONFIRMATION('pasture')}</div>
          </AppModal>

          <AppModal
            isOpen={state.isOpenConfirmationModal}
            confirmButtonText="Yes"
            handleCancel={toggleConfirmationModal}
            handleConfirm={handleGeofenceGroupUpdate}>
            <div className="py-4">
              One or more groups are already assigned to a different pasture. Do you want to update the pasture for
              these groups?
            </div>
          </AppModal>

          <UnsavedChangesModal
            isOpen={modalProps.visible}
            handleCancel={modalProps.onCancel}
            handleConfirm={modalProps.onSave}
            handleDiscard={modalProps.onDiscard}
          />
        </>
      )}
    </>
  );
}
